import { useAuth } from '@agentnet/auth';
import { Grid, GridProps, GridSize, TextFieldProps } from '@material-ui/core';
import { getCountiesPropertyFromList } from 'api/file/file-property-api';
import { CountyType, fileInfoType } from 'api/file/interfaces/get-file';
import useAsync from 'hooks/useAsync';
import * as _ from 'lodash';
import { ChangeEvent, FC, FocusEvent, useEffect, useMemo, useState } from 'react';
import {
  AddressFields,
  DomesticAddressFieldGroupProps,
  IDomesticAddressFields,
} from 'ui-kit/form/interfaces/AddressModal';
import AgentNetDropdownSelector, { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import { StateSelectField, StateSelectFieldProps } from 'ui-kit/inputs/StateSelectField';
import usStates from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import USPostalCodeField from 'ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { apnAddressSchema, domesticAddressSchema } from 'utilities/validation/schemas';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { ObjectSchema } from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { STATE_CODES } from 'features/constants';
import { ctTownToCounty } from './ct-town-county-map';
import './DomesticAddress.scss';

export type searchType = {
  id?: number | string | undefined | null;
  value: any;
  name: any;
  type?: ('primary' | 'secondary' | 'none') | string | undefined | null;
};
export interface AddressComponents {
  long_name: string;
  short_name: string;
  types: string[];
}
export interface IPlaceDetails {
  address_components: AddressComponents[];
  name: string;
  formatted_address: string;
  place_id: string;
}
export interface PlaceAddress {
  address?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  county?: string;
}

export type IDomesticAddressProps = DomesticAddressFieldGroupProps & {
  displayRequiredAddressFields?: boolean;
  isOutsideJurisdiction?: boolean;
  isFileInformation?: boolean;
  ContainerComponentProps?: GridProps;
  Address1Props?: TextFieldProps;
  Address2Props?: TextFieldProps;
  CityProps?: TextFieldProps;
  County?: TextFieldProps;
  PostalCodeProps?: TextFieldProps;
  StateProps?: StateSelectFieldProps;
  error?: string | undefined;
  value?: AddressFields;
  onChange?: (value: AddressFields) => void;
  onBlur?: (value: AddressFields) => void;
  fileInfo?: fileInfoType | null;
  isCreateFile?: boolean;
  postalCodeRequired?: boolean;
  showCounty?: boolean;
  disabled?: boolean;
  disableState?: boolean;
  showAllValidation?: boolean;
  schema?: ObjectSchema<ObjectShape>;
  addPropertyClicked?: boolean;
  setIsValid?: (isValid: boolean) => void;
  isCreateFileForm?: boolean;
  stateOrProvinceField?: boolean;
  stateOptions?: { name: string; abbreviation: string }[] | false;
  useGoogleAutocomplete?: boolean;
  qaAttributePrefix?: string;
  updatedQAAttributes?: AddressFields;
  parentValidation?: boolean;
  onCountyUpdate?: any;
  getAllState?: boolean;
  propertyIdentificationMethod?: string;
  accountId?: number | undefined;
  isPartyVerificationDrawer?: boolean;
  cityGridSize?: GridSize;
  allowedStates?: string[];
};

export const DomesticAddressField: FC<IDomesticAddressProps> = ({
  onChange,
  ContainerComponentProps,
  value,
  isOutsideJurisdiction,
  StateProps,
  displayRequiredAddressFields,
  error,
  disabled,
  disableState,
  showCounty = false,
  showAllValidation,
  schema,
  addPropertyClicked,
  useGoogleAutocomplete,
  stateOrProvinceField = true,
  setIsValid,
  stateOptions,
  qaAttributePrefix = '',
  parentValidation,
  isFileInformation = false,
  onCountyUpdate,
  getAllState,
  propertyIdentificationMethod = undefined,
  accountId,
  updatedQAAttributes,
  isPartyVerificationDrawer = false,
  cityGridSize,
  allowedStates,
}: IDomesticAddressProps): JSX.Element => {
  const newQAAttributes = qaAttributePrefix
    ? {
        address1: `${qaAttributePrefix}Address1`,
        address2: `${qaAttributePrefix}Address2`,
        city: `${qaAttributePrefix}City`,
        state: `${qaAttributePrefix}State`,
        postalCode: `${qaAttributePrefix}Zip`,
        county: `${qaAttributePrefix}County`,
      }
    : updatedQAAttributes;
  const [currentValue, setCurrentValue] = useState<AddressFields>(value ?? ({} as AddressFields));
  const { getAccessToken } = useAuth();
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const validateErrors = async (addressFields: IDomesticAddressFields, e: any) => {
    const errs =
      (await doValidate(
        addressFields,
        propertyIdentificationMethod === 'apn' ? apnAddressSchema : schema || domesticAddressSchema,
      )) || [];
    setValidationErrors(errs);
    setIsValid && setIsValid(errs.length === 0);
  };
  const isStateOrProvinceField = stateOrProvinceField ? 'stateOrProvince' : 'state';
  const onBlurValidation = async (addressFields: IDomesticAddressFields, e: any = null) => {
    validateErrors(addressFields, e);
  };
  const [counties, setCounties] = useState<SelectOption[]>([]);
  const getState = () => {
    const currentState: any = value?.stateOrProvince ? value?.stateOrProvince : value?.state ?? '';
    return typeof currentState !== 'string' ? currentState?.target?.value ?? '' : currentState ?? '';
  };

  const getCountiesPropertyFromListData = async (): Promise<CountyType> => {
    const token = await getAccessToken();
    const state = getState() || '';
    const response = await getCountiesPropertyFromList(state, accountId ?? 0, token);
    return response;
  };

  const { execute: propertyListExecute, value: propertyListResults } = useAsync<CountyType>(
    getCountiesPropertyFromListData,
    false,
  );

  useEffect(() => {
    const state = getState() || '';
    let shouldFetchCounties = false;
    if (showCounty) {
      if (isOutsideJurisdiction) {
        shouldFetchCounties = state !== '';
      } else {
        shouldFetchCounties = accountId !== undefined && accountId > 0;
      }
    }
    if (shouldFetchCounties) {
      propertyListExecute();
    }
  }, [accountId, value?.state, value?.stateOrProvince, isOutsideJurisdiction]);

  useEffect(() => {
    const state = getState() || '';
    if (showCounty && state !== '') {
      if (state != '') {
        const countyList: SelectOption[] = [];
        propertyListResults?.counties?.forEach((c) => {
          // CT: skip planning region names (e.g. "Capitol Planning Region").
          // Google returns these for administrative_area_level_2 instead of the
          // 8 traditional county names. They are not valid county values.
          if (state.toUpperCase() === STATE_CODES.CT && /region/i.test(c)) return;
          const option = {
            name: c,
            value: c,
          };
          countyList.push(option);
        });

        setCounties(countyList);
      }
    }
    return () => {
      setCounties([]);
    };
  }, [propertyListResults]);

  useEffect(() => {
    const normalizeCounty = (name: string) =>
      name
        .replace(/ County$/i, '')
        .replace(/ Parish$/i, '')
        .trim()
        .toLowerCase();

    if (counties.length > 0) {
      // CT fallback runs first: Google Places returns planning region names
      // (e.g. "Capitol Planning Region") for administrative_area_level_2 instead of the
      // 8 traditional county names because Connecticut abolished county government in 1960.
      // Only runs when the county is empty or not already a valid option — this allows the
      // user to manually change the county after auto-population without it being reset.
      if (getState().toUpperCase() === STATE_CODES.CT && currentValue?.city) {
        const isAlreadyValidCounty = currentValue?.county
          ? counties.some((c) => c.value && normalizeCounty(c.value) === normalizeCounty(currentValue.county ?? ''))
          : false;
        if (!isAlreadyValidCounty) {
          const ctCounty = ctTownToCounty[currentValue.city.toLowerCase()];
          const matchedCounty = ctCounty
            ? counties.find((c) => c.value && normalizeCounty(c.value) === ctCounty.toLowerCase())?.value
            : undefined;
          if (matchedCounty) {
            if (matchedCounty !== currentValue.county) {
              if (onCountyUpdate && isFileInformation) {
                onCountyUpdate && onCountyUpdate({ ...currentValue, county: matchedCounty });
              } else {
                onChange && onChange({ ...currentValue, county: matchedCounty });
              }
            }
            return;
          }
        }
      }

      // Standard normalization for all other states (and CT when city-based lookup found
      // no match): strips " County" / " Parish" suffix and matches case-insensitively.
      if (currentValue?.county) {
        const tempCounty = normalizeCounty(currentValue.county);
        const formattedCounty = counties.find(
          (countyOption) => countyOption.value && normalizeCounty(countyOption.value) === tempCounty,
        )?.value;
        if (formattedCounty !== undefined) {
          if (formattedCounty !== currentValue.county) {
            if (onCountyUpdate && isFileInformation) {
              onCountyUpdate && onCountyUpdate({ ...currentValue, county: formattedCounty });
            } else {
              onChange && onChange({ ...currentValue, county: formattedCounty });
            }
          }
          return;
        }
      }
    }

    if (counties.length === 1 && !isFileInformation) {
      onChange && onChange({ ...currentValue, county: counties[0]?.value });
    }
  }, [counties, currentValue?.address1, currentValue?.county]);

  useEffect(() => {
    const validateErrorscFunc = async () => {
      if (value && !_.isEqual(value, currentValue)) {
        setCurrentValue(value);
        validateErrors(value, null);
      }
    };
    validateErrorscFunc();
  }, [value, showAllValidation]);

  useEffect(() => {
    if (value) {
      validateErrors(value, null);
    }
  }, [parentValidation]);

  /* Check  validations - but render google api prior before validations happen */
  useEffect(() => {
    validationErrors && setIsValid && setIsValid(validationErrors.length === 0);
  }, [validationErrors]);

  useEffect(() => {
    validateErrors(currentValue, null);
  }, [propertyIdentificationMethod]);

  useEffect(() => {
    if (firstLoad) {
      currentValue && validateErrors(currentValue, null);
      setFirstLoad(false);
    }
  }, []);

  /* Takes the current value for state and iterates through the list of google predictions and separating predictions that match the current state
  into one array and all else into another - returns concationation of the array of the values corresponding to state and others  */

  /* Google spells PR and VI out in prediction descriptions instead of using their abbreviations */
  const toPredictionStateToken = (stateCode: string) => {
    const normalized = stateCode?.toLowerCase() ?? '';
    if (normalized === 'vi') {
      return 'usvi';
    }
    if (normalized === 'pr') {
      return 'puerto rico';
    }
    return normalized;
  };

  const predictionMatchesState = (description: string, stateToken: string) =>
    description?.split(',').some((addressItem) => addressItem.toLowerCase().trim() === stateToken) ?? false;

  const isStateAllowed = (stateCode?: string) =>
    !allowedStates?.length || allowedStates.some((state) => state.toLowerCase() === (stateCode ?? '').toLowerCase());

  const allowedStateOptions = useMemo(() => {
    if (!allowedStates?.length) {
      return undefined;
    }
    const allowedAbbreviations = allowedStates.map((state) => state.toUpperCase());
    return usStates.filter((state) => allowedAbbreviations.includes(state.abbreviation));
  }, [allowedStates]);

  const prioritizeStateOptions = (states: Array<{ name: string; value: string }>) => {
    const currentState = getState();
    const tempState = toPredictionStateToken(currentState);

    /* The Places API can only be restricted by country, so states are narrowed here instead */
    const allowedStateTokens = allowedStates?.map(toPredictionStateToken) ?? [];
    const selectableStates = allowedStateTokens.length
      ? states.filter((state) => allowedStateTokens.some((token) => predictionMatchesState(state?.name, token)))
      : states;

    const currentStateList: {
      name: string;
      value: string;
    }[] = [];

    const restStateList: {
      name: string;
      value: string;
    }[] = [];

    for (const state of selectableStates) {
      if (currentState && predictionMatchesState(state?.name, tempState)) {
        currentStateList.push(state);
      } else {
        restStateList.push(state);
      }
    }

    if (getAllState) {
      return [...currentStateList, ...restStateList];
    }
    return [...currentStateList];
  };
  const renderAutoCompleteAddressFields = () => {
    if (useGoogleAutocomplete) {
      return (
        <GoogleAutocompleteSearch
          variant="outlined"
          fullWidth
          required={displayRequiredAddressFields && propertyIdentificationMethod !== 'apn'}
          label="Address 1"
          id="address1"
          name="address1"
          data-testid={newQAAttributes?.address1}
          firstLoad={firstLoad}
          errs={validationErrors}
          orderOptions={prioritizeStateOptions}
          onChange={(e) => {
            if (e?.target?.value || e?.target?.value === '') {
              onChange && onChange({ ...currentValue, address1: e.target.value } as IDomesticAddressFields);
            }
          }}
          onPlaceSelect={(place: any) => {
            const currentState = getState();
            if (
              (currentState?.toLowerCase() === place?.state?.toLowerCase() || getAllState) &&
              isStateAllowed(place?.state)
            ) {
              onChange &&
                onChange({
                  ...currentValue,
                  address1: place?.address1 ?? '',
                  address2: place?.address2 ?? value?.address2 ?? '',
                  postalCode: place?.zipcode ?? '',
                  [isStateOrProvinceField]: place?.state ?? '',
                  city: place?.city ?? '',
                  // CT: Google returns planning region names (e.g. "Capitol Planning Region")
                  // for administrative_area_level_2, not traditional county names.
                  // Clear it here so the normalization effect resolves the correct county
                  // from the city/town name via ctTownToCounty.
                  county: place?.state?.toUpperCase() === STATE_CODES.CT ? '' : place?.county ?? '',
                });
            } else {
              onChange &&
                onChange({
                  ...currentValue,
                  address1: '',
                  postalCode: '',

                  city: '',
                  county: '',
                });
            }
          }}
          value={value?.address1 ?? ''}
          showValidation={showAllValidation}
          addPropertyClicked={addPropertyClicked}
          stateConstraint={getState()}
          disabled={disabled}
        />
      );
    } else {
      return (
        <AgentNetTextInput
          variant="outlined"
          fullWidth
          required={displayRequiredAddressFields && propertyIdentificationMethod !== 'apn'}
          label="Address 1"
          id="address1"
          name="address1"
          data-testid={newQAAttributes?.address1}
          firstLoad={firstLoad}
          errs={validationErrors}
          onChange={(e) => {
            onChange && onChange({ ...currentValue, address1: e.target.value } as IDomesticAddressFields);
          }}
          value={value?.address1 ?? ''}
          showValidation={showAllValidation}
          addPropertyClicked={addPropertyClicked}
          disabled={disabled}
        />
      );
    }
  };
  return (
    <Grid container spacing={3} {...ContainerComponentProps}>
      <Grid item xs={6}>
        {renderAutoCompleteAddressFields()}
      </Grid>

      {!isPartyVerificationDrawer && (
        <Grid item xs={6}>
          <AgentNetTextInput
            variant="outlined"
            name="address2"
            fullWidth
            label="Address 2"
            id="address2"
            data-testid={newQAAttributes?.address2}
            errs={validationErrors}
            firstLoad={firstLoad}
            onChange={(e) => {
              onChange && onChange({ ...currentValue, address2: e.target.value } as IDomesticAddressFields);
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              onBlurValidation({ ...currentValue, address2: e.target.value } as IDomesticAddressFields);
            }}
            value={value?.address2 ?? ''}
            showValidation={showAllValidation}
            addPropertyClicked={addPropertyClicked}
            disabled={disabled}
          />
        </Grid>
      )}
      <Grid item xs={cityGridSize ?? 3}>
        <AgentNetTextInput
          variant="outlined"
          required={displayRequiredAddressFields && propertyIdentificationMethod !== 'apn'}
          fullWidth
          label="City"
          id="city"
          name="city"
          data-testid={newQAAttributes?.city}
          firstLoad={firstLoad}
          errs={validationErrors}
          value={value?.city ?? ''}
          showValidation={showAllValidation}
          addPropertyClicked={addPropertyClicked}
          onChange={(e) => {
            onChange && onChange({ ...currentValue, city: e.target.value } as IDomesticAddressFields);
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            onBlurValidation({ ...currentValue, city: e.target.value } as IDomesticAddressFields);
          }}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={3}>
        <StateSelectField
          label="State"
          required={displayRequiredAddressFields}
          data-testid={newQAAttributes?.state}
          helperText={error}
          name={isStateOrProvinceField}
          id={isStateOrProvinceField}
          disabled={disableState || disabled}
          shouldDisableClearable={!isOutsideJurisdiction ? true : false}
          showValidation={showAllValidation}
          errs={validationErrors}
          stateOptions={stateOptions !== undefined ? stateOptions : allowedStateOptions}
          value={getState() ?? ''}
          {...StateProps}
          onChange={(e: string | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange &&
              onChange({
                ...currentValue,
                [isStateOrProvinceField]: typeof e === 'string' ? e : e.target.value,
              } as IDomesticAddressFields);
          }}
          onBlur={() => {
            onBlurValidation({ ...currentValue } as IDomesticAddressFields, isStateOrProvinceField);
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <USPostalCodeField
          variant="outlined"
          fullWidth
          name="postalCode"
          required={displayRequiredAddressFields && propertyIdentificationMethod !== 'apn'}
          InputLabelProps={{ shrink: true }}
          label={'Zip'}
          data-testid={newQAAttributes?.postalCode}
          id="postalCode"
          disabled={disabled}
          errs={validationErrors}
          showValidation={showAllValidation}
          onChange={(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            onChange && onChange({ ...currentValue, postalCode: e.target.value } as IDomesticAddressFields);
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            onBlurValidation({ ...currentValue, postalCode: e.target.value } as IDomesticAddressFields, 'postalCode');
          }}
          value={value?.postalCode ?? ''}
        />
      </Grid>

      {showCounty && (
        <Grid item xs={3}>
          <AgentNetDropdownSelector
            name="county"
            label="County"
            required={displayRequiredAddressFields}
            errs={validationErrors}
            options={counties}
            value={value?.county ?? ''}
            id="county"
            showValidation={showAllValidation}
            menuOption={(county: string) => onChange && onChange({ ...value, county: county })}
            dropdowntype="outlined"
            disabled={counties.length === 0 || disabled}
            data-testid={newQAAttributes?.county}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default DomesticAddressField;
